import React, { useEffect, useMemo, useState } from "react";
import { fnGetDataSet } from "@shared/api/restApi";
import { global } from "@shared/libs/Global";
import { formatNumber, toNum } from "@shared/utils/CommonUtils";
import SnChart from "@shared/components/Grid/SnChart";
import { snSysWidget014 } from "./icons";

interface Props {
  cdWidget?: string;
  nmWidget?: string;
  searchData?: any;
  refreshKey?: number;
}

type RejectRow = {
  no_rank?: number;
  nm_reject?: string;
  qt_b?: number;
  rt_share?: number;
  total_qt_b?: number;
};

const chartColors = [
  "#ef4444", // Red (토마토 레드)
  "#06b6d4", // Cyan (청록/시안)
  "#f97316", // Orange (오렌지)
  "#6366f1", // Indigo (남색/인디고)
  "#14b8a6", // Teal (민트/티일)
  "#a855f7", // Purple (퍼플)
];
const SnSysWidget014: React.FC<Props> = ({
  nmWidget,
  searchData,
  refreshKey,
}) => {
  const { userInfo } = global();
  const [rows, setRows] = useState<RejectRow[]>([]);

  const params = useMemo(
    () => ({
      cd_firm: searchData?.cd_firm || userInfo.cd_firm,
      dt: searchData?.dt,
    }),
    [searchData?.cd_firm, searchData?.dt, userInfo.cd_firm]
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data: any = await fnGetDataSet({
          id: "sn_sys_widget_014_01",
          params,
        });

        setRows(data?.[0] || []);
      } catch (error) {
        setRows([]);
      }
    };

    fetchData();
  }, [params, refreshKey]);

  const totalAmount = useMemo(() => {
    const total = toNum(rows?.[0]?.total_qt_b);
    return total || rows.reduce((sum, row) => sum + toNum(row.qt_b), 0);
  }, [rows]);

  const chartData = useMemo(() => {
    const topRows = rows.map((row) => ({
      nm_reject: row.nm_reject || "",
      qt_b: toNum(row.qt_b),
      rt_share: toNum(row.rt_share),
    }));
    const topAmount = topRows.reduce((sum, row) => sum + row.qt_b, 0);
    const etcAmount = Math.max(totalAmount - topAmount, 0);
    const etcShare = Math.max(
      100 - topRows.reduce((sum, row) => sum + row.rt_share, 0),
      0
    );

    return etcAmount > 0
      ? [
          ...topRows,
          {
            nm_reject: "기타",
            qt_b: etcAmount,
            rt_share: etcShare,
          },
        ]
      : topRows;
  }, [rows, totalAmount]);

  return (
    <div className="flex h-full min-h-[280px] w-full flex-col rounded-lg border border-gray-100 bg-white px-4 py-3 shadow-sm">
      <div className="mb-3 flex items-center gap-3">
        <div className="aspect-square h-10 shrink-0">
          <img
            src={snSysWidget014}
            alt=""
            className="h-full w-full object-contain"
          />
        </div>
        <div className="truncate text-lg font-semibold text-slate-900">
          {nmWidget}
        </div>
      </div>

      <div className="mb-2 flex items-center justify-between gap-4 px-1">
        <div className="flex items-baseline gap-4">
          <span className="text-base font-bold text-slate-900">불량수량</span>
          <span className="text-lg font-bold text-slate-950">
            {formatNumber(totalAmount, { fractionDigits: 0 })}
          </span>
        </div>
        <div className="min-w-[120px] text-center text-lg font-bold text-slate-900">
          Worst 5
        </div>
      </div>

      <div className="grid min-h-0 flex-1 grid-cols-[44%_56%] gap-3">
        <div className="min-h-0">
          <SnChart
            type="custom"
            data={chartData}
            height="100%"
            width="100%"
            seriesOptions={{
              series: [
                {
                  type: "donut",
                  angleKey: "qt_b",
                  calloutLabelKey: "nm_reject",
                  sectorLabelKey: "nm_reject",
                  fills: chartColors,
                  strokes: ["#ffffff"],
                  innerRadiusRatio: 0.58,
                  calloutLabel: { enabled: false },
                  sectorLabel: {
                    enabled: true,
                    color: "#0f172a",
                    fontSize: 11,
                    formatter: ({ datum }: any) =>
                      `${datum?.nm_reject || ""}\n${formatNumber(
                        toNum(datum?.qt_b),
                        {
                          fractionDigits: 0,
                        }
                      )}\n${formatNumber(toNum(datum?.rt_share), {
                        fractionDigits: 1,
                      })}%`,
                  },
                  tooltip: {
                    renderer: ({ datum }: any) => ({
                      title: datum?.nm_reject || "",
                      content: `${formatNumber(toNum(datum?.qt_b), {
                        fractionDigits: 0,
                      })} / ${formatNumber(toNum(datum?.rt_share), {
                        fractionDigits: 1,
                      })}%`,
                    }),
                  },
                },
              ],
            }}
            options={{
              padding: { top: 2, right: 2, bottom: 2, left: 2 },
              legend: { enabled: false },
            }}
          />
        </div>

        <div className="min-w-0 overflow-hidden rounded border border-slate-300 text-sm">
          <div className="grid grid-cols-[1fr_1fr_0.8fr] bg-slate-50 text-center font-semibold text-slate-900">
            <div className="border-b border-r border-slate-300 px-2 py-1">
              불량유형
            </div>
            <div className="border-b border-r border-slate-300 px-2 py-1">
              불량수량
            </div>
            <div className="border-b border-slate-300 px-2 py-1">점유율</div>
          </div>
          {rows.map((row, index) => (
            <div
              key={`${row.nm_reject || "reject"}-${index}`}
              className="grid grid-cols-[1fr_1fr_0.8fr] text-slate-900"
            >
              <div className="truncate border-b border-r border-slate-300 px-2 py-1">
                {row.nm_reject}
              </div>
              <div className="border-b border-r border-slate-300 px-2 py-1 text-right">
                {formatNumber(toNum(row.qt_b), { fractionDigits: 0 })}
              </div>
              <div className="border-b border-slate-300 px-2 py-1 text-right">
                {formatNumber(toNum(row.rt_share), { fractionDigits: 1 })}
              </div>
            </div>
          ))}
          {rows.length === 0 && (
            <div className="px-2 py-6 text-center text-sm text-slate-400">
              조회된 데이터가 없습니다.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SnSysWidget014;
